/**
 * Video-project content languages (dialogue / on-screen text).
 * Codes are BCP-47 / ISO 639 where practical; `ary` = Moroccan Arabic (Darija).
 */

export interface VideoProjectLanguageOption {
	value: string;
	label: string;
	/** Short English name for LLM instructions */
	englishName: string;
}

export const VIDEO_PROJECT_LANGUAGES: readonly VideoProjectLanguageOption[] = [
	{ value: "en", label: "English", englishName: "English" },
	{ value: "fr", label: "Français", englishName: "French" },
	{
		value: "ar",
		label: "العربية (فصحى)",
		englishName: "Modern Standard Arabic (Fusha)",
	},
	{
		value: "ary",
		label: "الدارجة المغربية",
		englishName: "Moroccan Arabic (Darija)",
	},
] as const;

const LOCALIZATION_TRANSCREATION_CORE = [
	"Do not translate word-for-word. Reformulate each line the way a native speaker would actually say it in that situation.",
	"Change wording, word order, and sentence rhythm whenever a literal rendering would sound foreign. The idea on that line must stay the same; the phrasing should not.",
	"Idioms, jokes, metaphors, insults, and cultural references should become local equivalents that land — keep the intent and emotion, not the source wording.",
	"If a native viewer would think \"this was translated\", the line is wrong — rewrite until it sounds locally written.",
] as const;

/** Shared UI/copy fields that must feel native, not calqued labels. */
export const LOCALIZATION_UI_COPY_RULES = [
	"title / logline: local pitch copy for the target audience — not a dictionary translation of the source title.",
	"onScreenText: short native headline or chyron (mobile-friendly); never a literal label translation.",
	"ctaLabel: what a local creator would button/link (platform-native tone).",
	"socialMediaFirstComment: what a local creator would pin — specific to this video, not a generic translated engagement bait.",
] as const;

export function normalizeVideoProjectLanguage(
	language: string | null | undefined,
): string {
	const raw = (language || "en").trim().toLowerCase().replace("_", "-");
	if (raw === "ar-ma" || raw === "ary-ma" || raw === "darija") return "ary";
	if (raw === "arb" || raw === "ar-msa") return "ar";
	return raw;
}

/**
 * Nova-3 `/listen` language. Pin a dialect when we know it; omit (auto-detect)
 * when the project language has no Deepgram code.
 * Darija uses `ar-MA`, not `ary`.
 */
export function deepgramLanguageForVideoProject(
	language: string | null | undefined,
): string | undefined {
	const code = normalizeVideoProjectLanguage(language);
	if (code === "ary") return "ar-MA";
	if (code === "ar") return "ar";
	if (code === "fr" || code.startsWith("fr-")) return "fr";
	if (code === "en" || code.startsWith("en-")) return "en";
}

export function videoProjectLanguageLabel(code: string): string {
	const normalized = normalizeVideoProjectLanguage(code);
	return (
		VIDEO_PROJECT_LANGUAGES.find((item) => item.value === normalized)
			?.label ?? code.toUpperCase()
	);
}

const RTL_LANGUAGE_PREFIXES = ["ar", "ary", "he", "fa", "ur"] as const;

/** True for Arabic (Fusha/Darija) and other RTL content languages. */
export function isVideoProjectRtl(
	language: string | null | undefined,
): boolean {
	const code = normalizeVideoProjectLanguage(language);
	return RTL_LANGUAGE_PREFIXES.some(
		(prefix) => code === prefix || code.startsWith(`${prefix}-`),
	);
}

/**
 * Authoring / translate instructions so dialogue sounds natural in the target language.
 */
export function getVideoProjectLanguageAuthoringRules(
	language: string | null | undefined,
): string {
	const code = normalizeVideoProjectLanguage(language);

	if (code === "ary") {
		return [
			"## Language: Moroccan Arabic — Darija (ary) — CRITICAL",
			"- locked.language / document.language is ary. This OVERRIDES the brief language and any narrator personality line that says English/French/Fusha.",
			"- Write ALL user-facing text in everyday Moroccan Darija (الدارجة المغربية): title, logline, onScreenText, dialogue — like a Moroccan talking on camera or a WhatsApp voice note, NOT like a TV dub or a novel.",
			"- DO NOT use Modern Standard Arabic (فصحى) except for rare fixed religious/formal quotes if the brief requires them — default is informal spoken Darija.",
			"- Forbidden calques: English/French/Fusha sentence structure with Darija words swapped in. If a Moroccan would not say it that way, throw the sentence away and rewrite it.",
			"- Prefer spoken particles and vocabulary: واش، شنو، علاش، كيفاش، بزاف، دابا، كاين، ماشي، غير، واخا، ياك، صافي، اللي، هاد. Avoid MSA defaults: هل، ماذا، ليس، يجب أن، الذي، بالنسبة لـ، نظرياً، قانونياً as default narration.",
			"- Short spoken lines. No poetic/novelistic metaphors or similes (no بحال شي مكتبة كتحرق / بحال معادلة / صمت أثقل من الجليد). Say the concrete action or feeling directly.",
			"- French loanwords only where Moroccans actually use them (le parking, l'autoroute). Do not Frenchify the whole line.",
			"- Prefer Arabic script (not Latin arabizi / Franco-Arabic) for dialogue and on-screen text.",
			"- Avoid Egyptian, Gulf, Levantine, or textbook Fusha patterns.",
			"- Keep the locked narrator personality expressed IN Darija (same attitude: sarcastic, funny, etc.).",
			"- Visual prompts (still & video generation): ALL image/video generation prompts (lookPrompt.prompt, plate.prompt, posterPrompt, and prompt.json details) MUST ALWAYS BE IN ENGLISH, regardless of the selected language. All spoken dialogue, titles, loglines, and on-screen copy must be Darija.",
			"- Example BAD: \"لا أستطيع أن أصدق هذا\" / \"بالنسبة ليها، كلشي كان أرقام\". Example GOOD: \"مايمكنش!\" / \"واش نتا كتهضر بصح؟\" / \"هي ما كتشوف غير الأرقام، حتى الموت\".",
		].join("\n");
	}

	if (code === "ar") {
		return [
			"## Language: Modern Standard Arabic — Fusha (ar)",
			"- locked.language / document.language is ar. This OVERRIDES the brief language and any narrator personality line that says English, French, or Darija.",
			"- Write ALL user-facing narration and on-screen text in clear Modern Standard Arabic (الفصحى) suitable for documentary / social video — spoken clarity, not literary archaism.",
			"- Do NOT switch into Moroccan Darija, Egyptian, or other colloquial dialects unless the brief explicitly asks.",
			"- Avoid English/French sentence structure with Arabic words swapped in. Use natural MSA connectors (في الواقع، من جهة أخرى، بالتالي، يبدو أن) only when a native broadcaster would.",
			"- Prefer short, direct narration lines. Replace foreign idioms with Arabic expressions that carry the same force — do not transliterate English metaphors.",
			"- Arabic script only. Visual prompts (still image & video generation prompts, lookPrompt, plate.prompt, posterPrompt, and prompt.json details) MUST ALWAYS BE IN ENGLISH; spoken/on-screen copy must be Arabic Fusha.",
			"- Example BAD (calque): \"I can't believe it\" → \"لا أستطيع أن أصدق ذلك\". Example GOOD: \"صعب التصديق!\" / \"من الصعب تصديق ما حدث\".",
			"- Example BAD: \"At the end of the day\" → \"في نهاية اليوم\" when meaning \"ultimately\". Example GOOD: \"في الأخير\" / \"في نهاية المطاف\".",
		].join("\n");
	}

	if (code === "fr" || code.startsWith("fr-")) {
		return [
			"## Language: French (fr)",
			"- locked.language / document.language is French. This OVERRIDES the brief language and any narrator personality line that says English or Arabic.",
			"- Write ALL user-facing narration and on-screen text in natural spoken French for short-form video — as heard in Francophone North Africa / Morocco (clear, direct, mobile-friendly), not stiff dubbing French or Parisian essay style.",
			"- Forbidden calques: English (or Arabic) sentence skeleton with French words inserted. If a Francophone viewer would not say it out loud, rewrite from scratch.",
			"- Prefer oral rhythm: short clauses, concrete verbs, natural links (bon, en fait, du coup, bref, au final, clairement) when they fit the tone — not filler on every line.",
			"- Recast idioms and jokes into French that lands locally; do not translate metaphors literally (e.g. \"piece of cake\" → not \"morceau de gâteau\").",
			"- onScreenText: punchy headline French, not a translated caption.",
			"- Visual prompts (still & video generation prompts, lookPrompt, plate.prompt, posterPrompt, and prompt.json details) MUST ALWAYS BE IN ENGLISH.",
			"- Example BAD (calque): \"It makes no sense\" → \"Ça ne fait pas de sens\". Example GOOD: \"Ça n'a aucun sens\" / \"C'est incompréhensible\".",
			"- Example BAD: \"What are you talking about?\" → \"De quoi parles-tu?\" (over-literal). Example GOOD: \"Tu racontes quoi là?\" / \"Tu dis n'importe quoi.\"",
		].join("\n");
	}

	if (code === "en" || code.startsWith("en-")) {
		return [
			"## Language: English (en)",
			"- Write ALL user-facing narration and on-screen text in natural spoken English for short-form video.",
			"- When localizing from French or Arabic, do not preserve source sentence shape — rewrite in idiomatic English a native would say on camera.",
			"- Recast foreign idioms into English equivalents; avoid \"translationese\" (awkward literal choices that no native speaker uses).",
			"- Visual prompts (still & video generation prompts, lookPrompt, plate.prompt, posterPrompt, and prompt.json details) MUST ALWAYS BE IN ENGLISH.",
		].join("\n");
	}

	const meta = VIDEO_PROJECT_LANGUAGES.find((item) => item.value === code);
	const name = meta?.englishName ?? code;
	return [
		`## Language: ${name} (${code})`,
		`- Write ALL user-facing narration and on-screen text in natural ${name}.`,
		"- Visual prompts (still image & video generation prompts, lookPrompt, plate.prompt, posterPrompt, and prompt.json details) MUST ALWAYS BE IN ENGLISH.",
	].join("\n");
}

/** Extra translate-system lines for a target language (transcreation, not calque). */
export function getVideoProjectLanguageTranslateRules(
	targetLanguage: string | null | undefined,
): string[] {
	const code = normalizeVideoProjectLanguage(targetLanguage);
	const shared = [
		"Read the FULL script first. Understand the story, relationships, jokes, and tone before you rewrite a single line.",
		...LOCALIZATION_TRANSCREATION_CORE,
		...LOCALIZATION_UI_COPY_RULES,
	];

	if (code === "ary") {
		return [
			...shared,
			"Output everyday Moroccan Darija (الدارجة المغربية) — never Fusha, Egyptian, Gulf, Levantine, or arabizi.",
			"Self-check every line: would a Moroccan say this in a WhatsApp voice note or on the street? If it sounds like a news bulletin, novel, or MSA textbook, rewrite it.",
			"Forbidden calques: same English/French/Fusha syntax with Darija words swapped in. If a Moroccan would not say it that way, throw the sentence away and rewrite it.",
			"Prefer spoken particles and vocabulary: واش، شنو، علاش، كيفاش، بزاف، دابا، كاين، ماشي، غير، واخا، ياك، صافي، اللي، هاد. Avoid MSA defaults: هل، ماذا، ليس، يجب أن، الذي، بالنسبة لـ، نظرياً، قانونياً، لا أستطيع، أعتقد أن.",
			"Sound informal and spoken — café / voice-note Darija — not literary narration or TV-dub Arabic.",
			"No poetic metaphors or novelistic similes in narration. Say the concrete action or feeling.",
			"French loanwords only where Moroccans actually use them (le parking, l'autoroute). Do not Frenchify the whole line.",
			"Keep Arabic script (not Latin arabizi). Prefer short lines; cut filler that only exists because of English sentence length.",
			"Example BAD (calque): \"It is raining cats and dogs\" → \"كاين كتسقطر القطط والكلاب\". Example GOOD: \"السما حلّات\" / \"كتصب بزاف\".",
			"Example BAD: \"I cannot believe this\" → \"لا أستطيع أن أصدق هذا\". Example GOOD: \"مايمكنش!\" / \"واش نتا كتهضر بصح؟\".",
			"Example BAD: \"She felt a coldness in her heart like a math equation explaining a disaster\". Example GOOD: \"حسّات ببرودة، وفهمات بلي هادشي كارثة\".",
			"Example BAD (Fusha slip): \"يجب أن نغادر الآن\". Example GOOD: \"خصنا نمشيو دابا\" / \"يلاه نمشيو\".",
		];
	}
	if (code === "ar") {
		return [
			...shared,
			"Output Modern Standard Arabic (Fusha) for narration — not Darija or other colloquial dialects.",
			"Self-check: would this sound natural on an Arabic news clip or documentary voice-over? If it reads like translated English, rewrite.",
			"Replace source idioms with Arabic expressions of equal force — never word-for-word metaphors.",
			"Example BAD (calque): \"He dropped the ball\" → literal ball imagery. Example GOOD: \"أخفق في مسؤوليته\" / \"تقصّر في دوره\".",
		];
	}
	if (code === "fr" || code.startsWith("fr-")) {
		return [
			...shared,
			"Write spoken French a Francophone North African audience would use on camera — not anglais traduit mot à mot.",
			"Self-check: read each line aloud. If it sounds like a subtitle track, rewrite with local rhythm and expressions.",
			"Recast humor, sarcasm, and insults so they land in French; keep the speaker's attitude.",
			"Example BAD: \"Let's cut to the chase\" → \"Coupons au chase\". Example GOOD: \"Allons droit au but\" / \"Sans détour\".",
			"Example BAD: \"I'm dead serious\" → \"Je suis mort sérieux\". Example GOOD: \"Je rigole pas\" / \"C'est du sérieux\".",
		];
	}
	if (code === "en" || code.startsWith("en-")) {
		return [
			...shared,
			"Write spoken English a native would use on camera — not a transparent calque of French or Arabic.",
			"Self-check: if any line sounds like \"translated English\", rewrite in idiomatic spoken English.",
		];
	}
	const meta = VIDEO_PROJECT_LANGUAGES.find((item) => item.value === code);
	const name = meta?.englishName ?? code;
	return [
		...shared,
		`Write natural spoken ${name} a native would use on camera, not a calque of the source.`,
	];
}

/**
 * Short TTS delivery hint so speech engines keep the document dialect
 * (especially Darija vs Fusha) instead of defaulting to MSA.
 */
export function getVideoProjectLanguageTtsInstruction(
	language: string | null | undefined,
): string | undefined {
	const code = normalizeVideoProjectLanguage(language);
	if (!code) return undefined;

	if (code === "ary") {
		return [
			"Language / dialect: Moroccan Arabic (Darija / الدارجة المغربية).",
			"Pronounce and deliver as spoken Moroccan Darija — informal café / voice-note speech — NOT Modern Standard Arabic (Fusha), Egyptian, Gulf, or Levantine.",
			"Keep natural Darija rhythm and particles (واش، شنو، دابا، بزاف، كاين).",
			"Do not elevate into formal news-anchor Arabic and do not rewrite the line into Fusha while speaking.",
			"Read the provided script text exactly as written.",
		].join(" ");
	}
	if (code === "ar") {
		return [
			"Language: Modern Standard Arabic (Fusha).",
			"Clear formal narration Arabic — not Darija or other colloquial dialects.",
			"Read the provided script text exactly as written.",
		].join(" ");
	}
	if (code === "fr" || code.startsWith("fr-")) {
		return [
			"Language: French.",
			"Speak natural spoken French for on-camera narration — not stiff literary French.",
			"Read the provided script text exactly as written.",
		].join(" ");
	}
	if (code === "en" || code.startsWith("en-")) {
		return [
			"Language: English.",
			"Speak clear natural English for a video soundtrack.",
			"Read the provided script text exactly as written.",
		].join(" ");
	}

	const meta = VIDEO_PROJECT_LANGUAGES.find((item) => item.value === code);
	const name = meta?.englishName ?? code;
	return [
		`Language: ${name}.`,
		`Speak natural ${name} for a video soundtrack.`,
		"Read the provided script text exactly as written.",
	].join(" ");
}

/**
 * Localize user-facing strings (native rewrite, same ideas).
 * Structure, ids, media URLs, and visual look prompts stay intact.
 */
export function buildTranslateSystemInstruction(input: {
	targetLanguage: string;
	sourceLanguage?: string;
	format?: string;
}): string {
	const format = (input.format ?? "").trim().toLowerCase();
	const source = normalizeVideoProjectLanguage(input.sourceLanguage);
	const target = normalizeVideoProjectLanguage(input.targetLanguage);
	const formatRules =
		format === "news" || format === "documentary"
			? [
					"News/documentary: keep facts, names, numbers, dates, places, and claims exact. Only rewrite how they are said so they sound native.",
				]
			: [
					"Story: you may recast jokes, insults, metaphors, and cultural shorthand so they land locally, as long as the plot beat and speaker intent stay the same.",
				];

	return [
		"You are a native copywriter localizing a finished video script. You are NOT a literal translator.",
		"A viewer who only speaks the target language must feel this was written for them — never that it was machine-translated.",
		`Source language: ${source}. Target language: ${target}.`,
		"The user message has two parts: (1) the full script to understand, (2) the JSON payload to fill. Read part 1 completely, then rewrite part 2.",
		"Transcreation rule: same story beat and speaker intent on every line; different words, rhythm, and local expressions whenever the source phrasing would feel foreign.",
		...LOCALIZATION_UI_COPY_RULES,
		"Return JSON only: the TranslationPayload object itself (keys: title, logline, ctaLabel?, socialMediaFirstComment?, characters, settings, props, continuityRules, narrativeConstraints, posterPrompt?, beats).",
		"Do NOT wrap the result in task/sourceLanguage/targetLanguage/payload — return the payload fields at the top level.",
		"Preserve every id exactly (beat ids, dialogue ids, character/setting/prop keys, continuity rule ids).",
		"Do not add, remove, merge, or split beats, dialogue lines, characters, settings, or props.",
		"Keep speaker and characterId as given. You may change only user-facing wording (text, names that are translatable, on-screen text, notes).",
		"Each line keeps the same speaker intent, emotion, and attitude — but you MUST reformulate whenever a literal translation would sound stiff or foreign.",
		"Do not invent new plot points, extra information, or a different conclusion.",
		"Keep proper nouns recognizable when the audience should still know them.",
		"Do not include media URLs, image look-prompt continuity fields, or audio fields.",
		"Visual prompts rule (CRITICAL): All visual prompts for still images and video generation (posterPrompt, lookPrompt, plate.prompt, prompt.json details) MUST ALWAYS remain in English. NEVER translate image or video prompts to non-English.",
		...formatRules,
		getVideoProjectLanguageAuthoringRules(input.targetLanguage),
		...getVideoProjectLanguageTranslateRules(input.targetLanguage),
	].join("\n");
}

export interface TranslateScriptLineInput {
	speaker?: string;
	characterId?: string;
	text: string;
}

export interface TranslateScriptBeatInput {
	id: string;
	kind?: string;
	onScreenText?: string;
	dialogue?: TranslateScriptLineInput[];
}

export interface TranslateScriptDocumentInput {
	title: string;
	logline: string;
	format?: string;
	language?: string;
	researchNotes?: string;
	narrator?: {
		name?: string;
		personality?: string;
		styleNotes?: string;
	};
	characters?: Record<
		string,
		{
			name?: string;
			role?: string;
			personality?: string;
		}
	>;
	beats?: TranslateScriptBeatInput[];
	continuityRules?: Array<{ id?: string; rule?: string }>;
}

/** Readable screenplay so the localizer understands the whole film before rewriting. */
export function buildTranslateScriptContext(
	document: TranslateScriptDocumentInput,
): string {
	const narratorName = document.narrator?.name?.trim() || "Narrator";
	const characters = document.characters ?? {};
	const lines: string[] = [
		`Title: ${document.title}`,
		`Logline: ${document.logline}`,
	];
	if (document.format) lines.push(`Format: ${document.format}`);
	if (document.language) lines.push(`Source language: ${document.language}`);

	const narratorBits = [narratorName];
	if (document.narrator?.personality?.trim())
		narratorBits.push(document.narrator.personality.trim());
	if (document.narrator?.styleNotes?.trim())
		narratorBits.push(document.narrator.styleNotes.trim());
	lines.push(`Narrator: ${narratorBits.join(" — ")}`);

	const characterLines = Object.entries(characters).map(([id, character]) => {
		const name = character.name?.trim() || id;
		const role = character.role?.trim();
		const personality = character.personality?.trim();
		return `- ${name}${role ? ` (${role})` : ""}${personality ? `: ${personality}` : ""}`;
	});
	if (characterLines.length)
		lines.push(`Characters:\n${characterLines.join("\n")}`);

	const notes = document.researchNotes?.trim();
	if (notes)
		lines.push(
			`Research notes:\n${notes.length > 2_000 ? `${notes.slice(0, 2_000).trim()}…` : notes}`,
		);

	const rules = (document.continuityRules ?? [])
		.map((rule) => rule.rule?.trim())
		.filter(Boolean);
	if (rules.length) lines.push(`Continuity:\n${rules.map((rule) => `- ${rule}`).join("\n")}`);

	lines.push("Script:");
	for (const [index, beat] of (document.beats ?? []).entries()) {
		const kind = beat.kind?.trim() ? ` · ${beat.kind.trim()}` : "";
		lines.push(`\n— Beat ${index + 1}${kind} (${beat.id}) —`);
		if (beat.onScreenText?.trim())
			lines.push(`[on-screen] ${beat.onScreenText.trim()}`);
		for (const line of beat.dialogue ?? []) {
			const text = line.text?.trim();
			if (!text) continue;
			let speaker = narratorName;
			if (line.speaker === "character") {
				const character = line.characterId
					? characters[line.characterId]
					: undefined;
				speaker =
					character?.name?.trim() || line.characterId || "Character";
			}
			lines.push(`${speaker}: ${text}`);
		}
	}

	return lines.join("\n").trim();
}

/** Two-step user message: understand the film, then fill the localization JSON. */
export function buildTranslateUserMessage(input: {
	sourceLanguage: string;
	targetLanguage: string;
	script: string;
	payload: unknown;
}): string[] {
	const source = normalizeVideoProjectLanguage(input.sourceLanguage);
	const target = normalizeVideoProjectLanguage(input.targetLanguage);
	const targetName =
		VIDEO_PROJECT_LANGUAGES.find((item) => item.value === target)
			?.englishName ?? target;

	return [
		[
			"STEP 1 — Understand the whole video. Do not translate yet.",
			`Source language: ${source}. You will localize into ${targetName} (${target}).`,
			"Read this entire script. Notice who is speaking, the tone, the jokes, and what each beat is doing.",
			input.script,
		].join("\n\n"),
		[
			`STEP 2 — Localize now into ${targetName}. Reformulate freely with local expressions so it sounds native — not a word-for-word translation.`,
			"Same ids, same beat/line count. Change phrasing, rhythm, idioms, and on-screen headlines until a local viewer would think this was written for them.",
			"Return JSON only (the TranslationPayload object).",
			JSON.stringify({
				task: "localize_video_project_payload",
				sourceLanguage: source,
				targetLanguage: target,
				payload: input.payload,
			}),
		].join("\n\n"),
	];
}
